import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { APP_NAME } from '../config';
import EnvironmentBadge from '../components/Layout/EnvironmentBadge';
import logger from '../utils/logger';

export const LoginPage = ({ onLogin }) => {
  const { t } = useTranslation(['auth', 'errors']);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onLogin(username, password);
    } catch (err) {
      logger.error('Login error:', err);
      const message =
        err?.userMessage ||
        (err?.status === 401 ? t('auth:errors.invalidCredentials') : t('auth:errors.generic'));
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const hasError = Boolean(error);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      <aside className="bg-primary-600 text-white px-6 py-10 md:flex-1 md:flex md:items-center md:justify-center md:py-0">
        <div className="max-w-sm text-center md:text-left">
          <h1 className="text-4xl font-extrabold tracking-tight md:text-5xl">{APP_NAME}</h1>
          <p className="mt-2 text-sm text-primary-100 md:text-base">{t('auth:login.tagline')}</p>
        </div>
      </aside>

      <main className="flex items-center justify-center px-4 py-10 md:flex-1">
        <div className="w-full max-w-sm">
          <div className="mb-4 flex justify-end">
            <EnvironmentBadge />
          </div>

          <h2 className="text-2xl font-bold text-gray-900">
            {t('auth:login.title')} {APP_NAME}
          </h2>

          {hasError && (
            <div
              id="login-error"
              role="alert"
              aria-live="polite"
              className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {error}
            </div>
          )}

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                {t('auth:login.username')}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                aria-invalid={hasError}
                aria-describedby={hasError ? 'login-error' : undefined}
                className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  hasError ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth:login.password')}
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  aria-invalid={hasError}
                  aria-describedby={hasError ? 'login-error' : undefined}
                  className={`block w-full rounded-md border px-3 py-2 pr-10 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    hasError ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? t('auth:password.hide') : t('auth:password.show')}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <EyeIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting && (
                <svg
                  className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                  />
                </svg>
              )}
              {submitting ? t('auth:submit.loading') : t('auth:login.submit')}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
